import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, TrendingUp, AlertTriangle, Building2, BarChart3, Users, ShieldCheck, ShieldAlert, Activity } from 'lucide-react';
import { useIFCarteiraResumo, type IFContratoResumo } from '../hooks/useIFCarteira';
import { useIFExecucoesNaoMedidas } from '../hooks/useIFExecucoesNaoMedidas';
import { useIFBoletinsPorStatus } from '../hooks/useIFBoletinsPorStatus';
import { useIFSparklineData } from '../hooks/useIFSparklineData';
import { useIFTriagem } from '../hooks/useIFTriagem';
import { IFAlertasBanner } from '../components/IFAlertasBanner';
import { IFTriagemPanel } from '../components/IFTriagemPanel';
import { ExecucoesNaoMedidasCard, BoletinsPorStatusCard, fmtBRLShort } from '../components/IFFiduciaryCards';
import { MiniSparkline } from '../components/MiniSparkline';
import { riskBadge, trendIcon, scoreColor } from '../components/IFRiskBadges';
import { PageHeader } from '@/components/system/PageHeader';
import { SectionCard } from '@/components/system/SectionCard';
import { DataTable, type Column } from '@/components/system/DataTable';
import { FilterBar } from '@/components/system/FilterBar';
import { MetricExplanationTooltip } from '../components/MetricExplanationTooltip';
import {
  explicacaoExposicao,
  explicacaoCapitalEmRisco,
  explicacaoCapitalProtegido,
  explicacaoIndiceCapitalQualificado,
  PROB_ATRASO_FALLBACK,
} from '../utils/metricExplanations';

const IFPainelPage: React.FC = () => {
  const navigate = useNavigate();
  const { resumos, isLoading, kpis } = useIFCarteiraResumo();
  const [search, setSearch] = useState('');
  const [filterRisco, setFilterRisco] = useState<string>('todos');
  const [periodoInicio, setPeriodoInicio] = useState<string>('');
  const [periodoFim, setPeriodoFim] = useState<string>('');

  const contratoIds = useMemo(() => resumos.map(r => r.contrato_id), [resumos]);
  const { data: execNaoMedidas, isLoading: loadExec } = useIFExecucoesNaoMedidas(contratoIds);
  const { data: boletinsStatus, isLoading: loadBol } = useIFBoletinsPorStatus(contratoIds);
  const { data: sparklineData } = useIFSparklineData(contratoIds);
  const alertasPorContrato = useMemo(() => new Map<string, number>(), []);
  const { items: triagem, oneLiner } = useIFTriagem(resumos, alertasPorContrato);

  const sparklineMap = useMemo(() => {
    const map = new Map<string, number[]>();
    if (sparklineData) {
      for (const s of sparklineData) {
        map.set(s.contrato_id, s.points.map(p => p.valor));
      }
    }
    return map;
  }, [sparklineData]);

  const filtered = useMemo(() => {
    let list = resumos;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(r => r.contrato_nome.toLowerCase().includes(q) || r.contratada.toLowerCase().includes(q));
    }
    if (filterRisco !== 'todos') {
      list = list.filter(r => {
        const c = r.classificacao_risco || '';
        if (filterRisco === 'critico') return c === 'crítico' || c === 'critico';
        return c === filterRisco;
      });
    }
    if (periodoInicio) {
      list = list.filter(r => r.ultimo_boletim_periodo_fim && r.ultimo_boletim_periodo_fim >= periodoInicio);
    }
    if (periodoFim) {
      list = list.filter(r => r.ultimo_boletim_periodo_fim && r.ultimo_boletim_periodo_fim <= periodoFim);
    }
    return list;
  }, [resumos, search, filterRisco, periodoInicio, periodoFim]);

  if (isLoading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  // ── Derived KPIs ───────────────────────────────────────────────
  const totalBac = filtered.reduce((s, r) => s + r.bac, 0);
  const totalEv = filtered.reduce((s, r) => s + r.ev, 0);
  const totalExposicao = Math.max(0, totalBac - totalEv);

  // Capital em Risco: Σ(exposição_i × prob_atraso_i), fallback 0.5
  const capitalEmRisco = filtered.reduce((s, r) => {
    const exposicao = Math.max(0, r.bac - r.ev);
    const prob = r.prob_atraso ?? PROB_ATRASO_FALLBACK;
    return s + Math.max(0, Math.min(exposicao * prob, exposicao));
  }, 0);

  // Capital Protegido Operacionalmente: Σ(exposição_i × score_i / 100)
  const capitalProtegido = filtered.reduce((s, r) => {
    const exposicao = Math.max(0, r.bac - r.ev);
    const score = r.score_risco ?? 0;
    return s + Math.max(0, Math.min(exposicao * (score / 100), exposicao));
  }, 0);

  // Índice de Capital Qualificado: média ponderada por exposição
  const icq = (() => {
    let somaScoreExposicao = 0;
    let somaExposicao = 0;
    filtered.forEach(r => {
      const exposicao = Math.max(0, r.bac - r.ev);
      const score = r.score_risco ?? 0;
      somaScoreExposicao += score * exposicao;
      somaExposicao += exposicao;
    });
    return somaExposicao > 0 ? somaScoreExposicao / somaExposicao : 0;
  })();

  // Aggregate prob_atraso (weighted by exposure) for tooltip
  const aggProbAtraso = (() => {
    let totalPeso = 0;
    let totalVal = 0;
    filtered.forEach(r => {
      const exp = Math.max(0, r.bac - r.ev);
      const prob = r.prob_atraso ?? PROB_ATRASO_FALLBACK;
      totalPeso += exp;
      totalVal += prob * exp;
    });
    return totalPeso > 0 ? totalVal / totalPeso : PROB_ATRASO_FALLBACK;
  })();

  const progressoMedio = filtered.length > 0
    ? filtered.reduce((s, r) => s + r.progresso_fisico_pct, 0) / filtered.length
    : 0;
  const contratadas = new Set(filtered.map(r => r.contratada));
  const contratosEmAlerta = filtered.filter(r =>
    r.classificacao_risco === 'crítico' || r.classificacao_risco === 'critico' || r.classificacao_risco === 'alto'
  ).length;

  const daysSince = (dateStr: string | null) => {
    if (!dateStr) return null;
    const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86_400_000);
    return diff >= 0 ? diff : null;
  };

  const columns: Column<IFContratoResumo>[] = [
    { key: 'contrato', label: 'Contrato', render: r => <span className="font-medium max-w-[200px] truncate block">{r.contrato_nome}</span> },
    { key: 'contratada', label: 'Contratada', render: r => <span className="text-sm text-muted-foreground max-w-[150px] truncate block">{r.contratada}</span> },
    { key: 'valor', label: 'Valor (R$)', align: 'right', render: r => (
      <span className="text-sm font-medium tabular-nums">{fmtBRLShort(r.valor_global)}</span>
    )},
    { key: 'curva', label: 'Curva S', align: 'center', render: r => (
      <MiniSparkline data={sparklineMap.get(r.contrato_id) || []} />
    )},
    { key: 'progresso', label: 'Progresso', align: 'center', render: r => (
      <div className="flex items-center gap-2 justify-center">
        <Progress value={Math.min(100, r.progresso_fisico_pct)} className="h-1.5 w-16" />
        <span className="text-xs font-medium">{r.progresso_fisico_pct.toFixed(1)}%</span>
      </div>
    )},
    { key: 'risco', label: 'Risco', align: 'center', render: r => riskBadge(r.classificacao_risco) },
    { key: 'tendencia', label: 'Tendência', align: 'center', render: r => trendIcon(r.tendencia) },
    { key: 'medicao', label: 'Inércia', align: 'right', render: r => {
      const days = daysSince(r.ultimo_boletim_periodo_fim);
      if (days == null) return <span className="text-muted-foreground text-sm">—</span>;
      const color = days > 60 ? 'text-destructive font-semibold' : days > 30 ? 'text-[hsl(var(--warning))] font-medium' : 'text-muted-foreground';
      return <span className={`text-sm tabular-nums ${color}`}>{days}d</span>;
    }},
  ];

  return (
    <div className="space-y-6">
        <PageHeader
          title="Painel Fiduciário"
          subtitle="Monitoramento de risco operacional — visão executiva da carteira"
        />

        <IFAlertasBanner />
        <IFTriagemPanel items={triagem} oneLiner={oneLiner} />

        {/* ── Hero KPIs: Modelo Fiduciário (4 cards) ──────────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {/* 1. Exposição Real */}
          <Card className="border-border/40 bg-card">
            <CardContent className="pt-5 pb-4">
              <MetricExplanationTooltip explicacao={explicacaoExposicao(totalBac, totalEv)}>
                <div className="text-muted-foreground text-[10px] font-semibold tracking-wider uppercase flex items-center gap-2">
                  <ShieldAlert className="h-3.5 w-3.5" />Exposição Real
                </div>
              </MetricExplanationTooltip>
              <p className="text-3xl lg:text-4xl font-bold mt-2 leading-none tabular-nums font-display">
                {fmtBRLShort(totalExposicao)}
              </p>
              <p className="text-xs text-muted-foreground mt-1.5">Saldo não executado · {filtered.length} contrato(s)</p>
            </CardContent>
          </Card>

          {/* 2. Capital Protegido Operacionalmente */}
          <Card className="border-border/40 bg-card">
            <CardContent className="pt-5 pb-4">
              <MetricExplanationTooltip explicacao={explicacaoCapitalProtegido(totalBac, totalEv, icq)}>
                <div className="text-muted-foreground text-[10px] font-semibold tracking-wider uppercase flex items-center gap-2">
                  <ShieldCheck className="h-3.5 w-3.5" />Capital Protegido
                </div>
              </MetricExplanationTooltip>
              <p className="text-3xl lg:text-4xl font-bold mt-2 leading-none tabular-nums font-display text-primary">
                {fmtBRLShort(capitalProtegido)}
              </p>
              <p className="text-xs text-muted-foreground mt-1.5">
                {totalExposicao > 0
                  ? `${((capitalProtegido / totalExposicao) * 100).toFixed(1)}% da exposição`
                  : 'Sem exposição calculada'}
              </p>
            </CardContent>
          </Card>

          {/* 3. Capital em Risco */}
          <Card className={`border-border/40 ${capitalEmRisco > 0 ? 'border-destructive/30 bg-destructive/[0.03]' : 'bg-card'}`}>
            <CardContent className="pt-5 pb-4">
              <MetricExplanationTooltip explicacao={explicacaoCapitalEmRisco(totalBac, totalEv, aggProbAtraso)}>
                <div className="text-muted-foreground text-[10px] font-semibold tracking-wider uppercase flex items-center gap-2">
                  <ShieldAlert className="h-3.5 w-3.5" />Capital em Risco
                </div>
              </MetricExplanationTooltip>
              <p className={`text-3xl lg:text-4xl font-bold mt-2 leading-none tabular-nums font-display ${capitalEmRisco > 0 ? 'text-destructive' : ''}`}>
                {fmtBRLShort(capitalEmRisco)}
              </p>
              <p className="text-xs text-muted-foreground mt-1.5">
                {totalExposicao > 0
                  ? `${((capitalEmRisco / totalExposicao) * 100).toFixed(1)}% da exposição`
                  : 'Sem exposição calculada'}
              </p>
            </CardContent>
          </Card>

          {/* 4. Índice de Capital Qualificado */}
          <Card className="border-border/40 bg-card">
            <CardContent className="pt-5 pb-4">
              <MetricExplanationTooltip explicacao={explicacaoIndiceCapitalQualificado(icq, totalExposicao)}>
                <div className="text-muted-foreground text-[10px] font-semibold tracking-wider uppercase flex items-center gap-2">
                  <Activity className="h-3.5 w-3.5" />Índice de Capital Qualificado
                </div>
              </MetricExplanationTooltip>
              <p className={`text-3xl lg:text-4xl font-bold mt-2 leading-none tabular-nums font-display ${scoreColor(icq)}`}>
                {icq.toFixed(1)}%
              </p>
              <p className="text-xs text-muted-foreground mt-1.5">
                score ponderado por exposição
              </p>
            </CardContent>
          </Card>
        </div>

        {/* ── KPI Row 2: Operational metrics ──────────────────────────── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Card className="border-border/40"><CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-2 text-muted-foreground text-[10px] font-semibold tracking-wider uppercase"><TrendingUp className="h-3.5 w-3.5" />Progresso Médio</div>
            <p className="text-xl font-bold mt-1.5 leading-none">{progressoMedio.toFixed(1)}%</p>
            <Progress value={Math.min(100, progressoMedio)} className="h-1 mt-2" />
          </CardContent></Card>
          <Card className="border-border/40"><CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-2 text-muted-foreground text-[10px] font-semibold tracking-wider uppercase"><BarChart3 className="h-3.5 w-3.5" />Contratadas</div>
            <p className="text-xl font-bold mt-1.5 leading-none">{contratadas.size}</p>
            <p className="text-[11px] text-muted-foreground/70 mt-1">empresas na carteira</p>
          </CardContent></Card>
          <Card className="border-border/40"><CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-2 text-muted-foreground text-[10px] font-semibold tracking-wider uppercase"><AlertTriangle className="h-3.5 w-3.5" />Em Alerta</div>
            <p className="text-xl font-bold mt-1.5 leading-none text-destructive">{contratosEmAlerta}</p>
            <p className="text-[11px] text-muted-foreground/70 mt-1">risco crítico/alto</p>
          </CardContent></Card>
          <ExecucoesNaoMedidasCard data={execNaoMedidas} isLoading={loadExec} />
        </div>

        <BoletinsPorStatusCard dataByContrato={boletinsStatus} isLoading={loadBol} />

        {filtered.length > 0 && (
          <SectionCard title="Contratadas na Carteira" icon={Users}>
            <div className="space-y-3">
              {Array.from(new Map(filtered.map(r => [r.contratada, r])).entries())
                .map(([emp]) => {
                  const contracts = filtered.filter(r => r.contratada === emp);
                  const avgScore = contracts.reduce((s, c) => s + (c.score_risco ?? 0), 0) / contracts.length;
                  return { emp, count: contracts.length, avgScore };
                })
                .sort((a, b) => b.count - a.count)
                .slice(0, 5)
                .map(c => (
                  <div key={c.emp} className="flex items-center gap-3">
                    <span className="text-sm flex-1 truncate">{c.emp}</span>
                    <span className="text-xs text-muted-foreground">{c.count} contrato(s)</span>
                    <Badge variant="outline" className="text-xs">Score: {c.avgScore.toFixed(0)}</Badge>
                  </div>
                ))}
            </div>
          </SectionCard>
        )}

        <SectionCard title="Contratos Monitorados" icon={Building2}>
          <FilterBar className="mb-4">
            <Input
              placeholder="Buscar contrato ou contratada..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="max-w-xs h-8 text-sm"
            />
            <Select value={filterRisco} onValueChange={setFilterRisco}>
              <SelectTrigger className="w-[160px] h-8 text-sm">
                <SelectValue placeholder="Classificação" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="critico">Crítico</SelectItem>
                <SelectItem value="alto">Alto</SelectItem>
                <SelectItem value="moderado">Moderado</SelectItem>
                <SelectItem value="baixo">Baixo</SelectItem>
              </SelectContent>
            </Select>
            <Input type="date" value={periodoInicio} onChange={e => setPeriodoInicio(e.target.value)} className="w-[150px] h-8 text-sm" />
            <Input type="date" value={periodoFim} onChange={e => setPeriodoFim(e.target.value)} className="w-[150px] h-8 text-sm" />
          </FilterBar>
          <DataTable
            columns={columns}
            data={filtered}
            keyExtractor={r => r.contrato_id}
            emptyText="Nenhum contrato encontrado com os filtros selecionados."
            onRowClick={r => navigate(`/financiamento/contrato/${r.contrato_id}`)}
          />
        </SectionCard>
    </div>
  );
};

export default IFPainelPage;
